import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ChevronLeft, ChevronRight, Loader2, Check, CalendarDays } from 'lucide-react';
import { safeFetch } from '../../utils/safeFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const DriverAvailability = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [savedDates, setSavedDates] = useState(new Set());

  const token = localStorage.getItem('driver_token');

  const fetchAvailability = useCallback(async () => {
    try {
      setLoading(true);
      const result = await safeFetch(`${API_URL}/api/go/availability`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (result.ok) {
        const dates = new Set(result.data.availability_dates || []);
        setSelectedDates(dates);
        setSavedDates(new Set(dates));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAvailability(); }, [fetchAvailability]);

  const toggleDate = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(dateStr + 'T00:00:00');
    if (date < today) return; // Can't select past dates

    setSelectedDates(prev => {
      const next = new Set(prev);
      if (next.has(dateStr)) next.delete(dateStr);
      else next.add(dateStr);
      setHasChanges(true);
      return next;
    });
  };

  const saveAvailability = async () => {
    setSaving(true);
    try {
      const result = await safeFetch(`${API_URL}/api/go/availability`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ dates: Array.from(selectedDates) })
      });
      if (result.ok) {
        const dates = new Set(result.data.availability_dates || []);
        setSavedDates(dates);
        setSelectedDates(dates);
        setHasChanges(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Generate calendar days
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDow = (firstDay.getDay() + 6) % 7; // Mon=0

  const days = [];
  for (let i = 0; i < startDow; i++) days.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) days.push(d);

  const prevMonth = () => setCurrentMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(year, month + 1, 1));

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().split('T')[0];

  // Count available days this month
  const monthAvailable = Array.from(selectedDates).filter(d => d.startsWith(`${year}-${String(month + 1).padStart(2, '0')}`)).length;

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#10B981]" /></div>;

  return (
    <div className="space-y-6" data-testid="driver-availability">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Availability</h1>
          <p className="text-[#64748B] text-sm mt-1">Select the days you're available for work</p>
        </div>
        {hasChanges && (
          <Button
            onClick={saveAvailability}
            disabled={saving}
            className="bg-[#10B981] hover:bg-[#059669] text-white"
            data-testid="save-availability-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="border-0 bg-[#1E293B]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#10B981]/10 flex items-center justify-center">
              <CalendarDays className="w-5 h-5 text-[#10B981]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{monthAvailable}</p>
              <p className="text-xs text-[#64748B]">Days this month</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 bg-[#1E293B]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#3B82F6]/10 flex items-center justify-center">
              <Check className="w-5 h-5 text-[#3B82F6]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{selectedDates.size}</p>
              <p className="text-xs text-[#64748B]">Total days selected</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calendar */}
      <Card className="border-0 bg-[#1E293B]">
        <CardContent className="p-5">
          {/* Month nav */}
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" size="sm" onClick={prevMonth} className="text-[#94A3B8] hover:text-white hover:bg-[#334155]" data-testid="prev-month">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <h2 className="text-lg font-semibold text-white" data-testid="current-month">
              {MONTHS[month]} {year}
            </h2>
            <Button variant="ghost" size="sm" onClick={nextMonth} className="text-[#94A3B8] hover:text-white hover:bg-[#334155]" data-testid="next-month">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {DAYS.map(d => (
              <div key={d} className="text-center text-xs font-medium text-[#64748B] py-2">{d}</div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((day, i) => {
              if (day === null) return <div key={`empty-${i}`} />;

              const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const isSelected = selectedDates.has(dateStr);
              const isToday = dateStr === todayStr;
              const isPast = new Date(dateStr + 'T00:00:00') < today;

              return (
                <button
                  key={dateStr}
                  onClick={() => toggleDate(dateStr)}
                  disabled={isPast}
                  className={`
                    relative h-12 rounded-lg text-sm font-medium transition-all
                    ${isPast
                      ? 'text-[#334155] cursor-not-allowed'
                      : isSelected
                        ? 'bg-[#10B981] text-white shadow-lg shadow-[#10B981]/20 hover:bg-[#059669]'
                        : 'text-[#CBD5E1] hover:bg-[#334155]'
                    }
                    ${isToday ? 'ring-2 ring-[#10B981]/50' : ''}
                  `}
                  data-testid={`day-${dateStr}`}
                >
                  {day}
                  {isSelected && !isPast && (
                    <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-white" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-6 mt-6 pt-4 border-t border-[#334155]">
            <div className="flex items-center gap-2 text-xs text-[#64748B]">
              <div className="w-4 h-4 rounded bg-[#10B981]" /> Available
            </div>
            <div className="flex items-center gap-2 text-xs text-[#64748B]">
              <div className="w-4 h-4 rounded bg-[#334155]" /> Unavailable
            </div>
            <div className="flex items-center gap-2 text-xs text-[#64748B]">
              <div className="w-4 h-4 rounded ring-2 ring-[#10B981]/50" /> Today
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DriverAvailability;
